import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  CountryField,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  ChangePlayerCountryMutation,
  ChangePlayerCountryMutationVariables,
  PlayerCountryFormQuery,
} from './__generated__/PlayerCountryForm';

const query = graphql`
  query SanityPlayerCountryForm {
    sanityPlayerCountryForm {
      title {
        ...SanityLocaleString
      }
      country {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const playerQuery = gql`
  query PlayerCountryForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      city
      countryCode
      street
      zipCode
    }
  }
`;

const playerMutation = gql`
  mutation ChangePlayerCountry(
    $playerId: ID!
    $zipCode: String!
    $city: String!
    $street: String!
    $countryCode: String!
  ) {
    changeAddress(
      playerId: $playerId
      zipCode: $zipCode
      city: $city
      street: $street
      countryCode: $countryCode
    ) {
      id
      city
      countryCode
      street
      zipCode
    }
  }
`;

const PlayerCountryForm: FC<{ playerId: string }> = ({ playerId }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerCountryFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerCountryForm;

  const [{ data }] = useQuery<PlayerCountryFormQuery>({
    query: playerQuery,
    variables: { playerId },
  });

  const [changePlayerAddressState, changePlayerAddress] = useMutation<
    ChangePlayerCountryMutation,
    ChangePlayerCountryMutationVariables
  >(playerMutation);

  const defaultValues = {
    countryCode: data?.player?.countryCode,
  };

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (values: typeof defaultValues) => {
    if (!data?.player?.id || !values.countryCode) {
      return;
    }

    changePlayerAddress({
      playerId: data?.player.id,
      city: data.player.city ?? '',
      countryCode: values.countryCode,
      street: data.player.street ?? '',
      zipCode: data.player.zipCode ?? '',
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form || !data) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <CountryField
            title={t(form.country)}
            name="countryCode"
            id="PlayerCountryForm__countryCode"
            className="col-span-full"
          />

          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={changePlayerAddressState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerCountryForm;
